import { Story } from '../../types/story';

export const lisbonNeighbor: Story = {
  id: 'lisbon-neighbor',
  title: 'The Neighbor',
  excerpt: 'A recently divorced woman in Lisbon\'s Alfama district discovers that her young musician neighbor is more than just a noise complaint.',
  city: 'Lisbon',
  publishDate: '2025-12-01',
  readTime: '27 min read',
  tags: ['♀♂', 'age gap', 'neighbors', 'seduction'],
  categories: ['forbidden', 'romantic', 'intense'],
  intensity: 3,
  content: `The guitar started at eleven PM, just as I was settling into bed with a book. Classical, beautifully played, but loud enough to seep through the walls of my apartment in Alfama. I tried to ignore it, but after twenty minutes, I gave up.

I'd been in Lisbon for three months, living in this beautiful but crumbling building with its azulejo tiles and narrow staircases. The divorce had been finalized in June, and I'd needed to get away from Madrid, from the life I'd built with a man I'd stopped loving years ago. At forty-five, I was starting over, and most days that felt liberating. But some nights, like this one, it just felt lonely.

I pulled on a robe over my pajamas and went next door. The music was coming from 3B, the apartment that had been empty when I moved in. I knocked, harder than I meant to.

The music stopped. Footsteps. The door opened, and I found myself looking at a young man—mid-twenties, maybe—with dark curly hair and the kind of face that belonged in a Caravaggio painting. He was shirtless, wearing only sweatpants, and holding a classical guitar.

"Sim?" he said, then switched to English when he saw my expression. "Yes?"

"It's eleven PM. Some of us are trying to sleep."

"Oh. Sorry. I didn't realize it was so late." His accent was Portuguese, his English good but careful. "I just got back from a gig. I was practicing."

"Well, could you practice during the day? Or at least before midnight?"

"Of course. I'm sorry. I'm Miguel, by the way. I just moved in last week."

"Sofia." I didn't offer my hand. "Please keep it down."

I went back to my apartment, feeling guilty for being so harsh. He'd been polite, apologetic. But I was tired and lonely and not in the mood to be understanding.

The next night, the guitar started again at eleven. I lay in bed, fuming, debating whether to go over there again or just suffer through it. But then I realized—he was playing Tárrega. "Recuerdos de la Alhambra." One of my favorite pieces.

I got up and went to his door. This time when he answered, he was wearing a shirt.

"I'm sorry," he said immediately. "I know I said I'd stop, but I have an audition tomorrow and I need to practice this piece and—"

"You're playing Tárrega."

"Yes. Do you know it?"

"My father used to play it. He was a guitarist. Not professional, but very good."

Miguel's face lit up. "Really? Would you like to come in? I could play it for you properly. Not through the wall."

I should have said no. Should have just asked him to keep it down and gone back to my apartment. But I was lonely, and he was beautiful, and it had been so long since anyone had looked at me with that kind of enthusiasm.

"Okay. But just for a few minutes."

His apartment was smaller than mine, a studio with a mattress on the floor and a guitar case in the corner. There were posters on the walls—concert advertisements, mostly—and sheet music scattered everywhere.

"Sorry for the mess. I'm still unpacking." He gestured to a chair—the only chair—and sat on the bed with his guitar. "So, your father played?"

"Yes. He died five years ago. Heart attack. He was only sixty-two."

"I'm sorry."

"It's okay. Well, it's not okay, but... you know." I settled into the chair. "Play for me. The Tárrega."

He did, and it was beautiful. Not perfect—there were a few missed notes, a couple of rough transitions—but passionate and sincere. When he finished, I realized I was crying.

"That was terrible," he said, misreading my tears. "I'm sorry, I'm still learning—"

"No. It was beautiful. I just... I haven't heard that piece in years. My father used to play it for my mother. It was their song."

We talked for hours after that. He told me about his music, about studying at the conservatory, about trying to make it as a classical guitarist in a world that didn't value classical music anymore. I told him about my divorce, about leaving Madrid, about feeling like I was starting my life over at an age when I should have had everything figured out.

"Forty-five isn't old," he said.

"It is when you're twenty-six."

"I'm twenty-seven, actually. And I don't think age matters. Not really. My grandmother always said that people are only as old as they feel."

"Your grandmother sounds wise."

"She was. She died last year." He set down his guitar. "Would you like some wine? I have a bottle of vinho verde."

We drank wine and talked until two in the morning. When I finally went back to my apartment, I felt lighter than I had in months.

The next night, he knocked on my door around ten.

"I'm going to practice," he said. "But I wanted to invite you over first. So you could hear it properly instead of through the wall."

It became a routine. Every night, he'd knock, I'd come over, and he'd play for me. Sometimes we'd talk afterward, sometimes we'd just sit in comfortable silence. He was easy to be with—no expectations, no judgment, just genuine interest in who I was.

After two weeks, I invited him to my place for dinner. I cooked bacalhau, the way my mother had taught me, and he brought wine and flowers.

"You didn't have to bring flowers," I said, but I was pleased.

"My mother taught me to never show up empty-handed."

We ate on my small balcony, looking out over the terracotta roofs of Alfama, the Tagus River glinting in the distance. The conversation flowed easily, and I found myself laughing more than I had in years.

"Can I ask you something?" he said as we finished the wine.

"Of course."

"Why did you get divorced? You don't have to answer if it's too personal."

I thought about it. "We grew apart. Or maybe we were never really together in the first place. We got married young—I was twenty-three, he was twenty-five. We thought we were in love, but really we were just... compatible. Good on paper. And for a while, that was enough. But eventually, I realized I wanted more. I wanted passion. I wanted to feel alive."

"And do you? Feel alive?"

"Sometimes. More now than I did in Madrid."

He reached across the table and took my hand. "I think you're beautiful. I know I shouldn't say that—you're my neighbor, you're older than me, it's probably inappropriate. But I think about you all the time. When I'm playing, I imagine you listening. When I'm at a gig, I wonder what you're doing. And I know this is crazy, but I can't help it."

My heart was racing. "Miguel—"

"You don't have to say anything. I just needed you to know."

But I did want to say something. I wanted to tell him that I thought about him too, that I'd started looking forward to our nightly sessions, that I felt more alive with him than I had in my entire marriage. Instead, I leaned across the table and kissed him.

He kissed me back, tentative at first, then more confident. His hand came up to cup my face, and I felt something ignite inside me that I'd thought was long dead.

"Come inside," I said.

We moved to my bedroom, and I was suddenly self-conscious about my body—the stretch marks from a pregnancy that had ended in miscarriage, the softness that came with age, the grey in my hair that I'd stopped dyeing. But the way he looked at me made me feel beautiful.

"You're shaking," he said softly.

"I'm nervous. It's been a while. And you're so young—"

"Stop. Age doesn't matter. You're beautiful. You're intelligent. You're everything I want."

He undressed me slowly, reverently, kissing every inch of skin he revealed. When I was naked, he stepped back and just looked at me.

"Perfect," he said.

I pulled him to me, undressing him in turn. His body was young and lean, all smooth skin and hard muscle. When I wrapped my hand around his cock, he gasped.

"I want you," I said. "But you need to tell me what you like. I'm not used to... I don't know what young men want."

"I want you to teach me. Show me what you like. I want to learn your body."

We fell onto the bed, and I did teach him. Showed him where to touch, how fast, how hard. He was an eager student, paying attention to every gasp, every moan, adjusting his technique based on my reactions.

When his mouth found my clit, I nearly came immediately. He was enthusiastic but inexperienced, and I guided him with my hands and my words.

"Slower. Yes, like that. Now use your fingers. Two. Curl them up. Oh god, yes, right there."

He learned quickly, and when I came, crying out in Portuguese, he looked up at me with such pride and satisfaction that I laughed.

"Good?" he asked.

"Very good. Now come here."

I pulled him up and kissed him, tasting myself on his lips. Then I rolled him onto his back and straddled him.

"Do you have a condom?"

"In my wallet. In my jeans."

I retrieved it, rolled it onto him, and sank down slowly. He was thick and hard, and the stretch was delicious. We both moaned.

"Fuck," he gasped. "You feel incredible."

I started to move, slowly at first, then faster. His hands found my breasts, playing with my nipples, and I rode him harder.

"Touch yourself," he said. "I want to watch you come again."

I did, my fingers finding my clit, and the combination of his cock inside me and my own touch pushed me over the edge. I came hard, my body clenching around him, and he followed moments later, groaning my name.

I collapsed onto his chest, both of us breathing hard.

"That was..." he started.

"Yeah."

We lay there for a while, then made love again, slower this time. He was more confident now, more willing to take charge, and I let him. Let him flip me onto my stomach and take me from behind, his hands gripping my hips, his cock hitting deep.

Afterward, we lay tangled together, and I felt something I hadn't felt in years—satisfied. Not just physically, but emotionally. Like I'd found something I didn't know I was looking for.

"Stay," I said. "Tonight. Stay with me."

"Okay."

He did stay, and the next night, and the night after that. We fell into a pattern—he'd practice, I'd listen, we'd make love, we'd fall asleep tangled together. My neighbors probably knew, but I didn't care. For the first time in years, I was happy.

"What are we doing?" I asked him one night, a month into whatever this was.

"What do you mean?"

"This. Us. You're twenty-seven. I'm forty-five. This can't be serious."

"Why not?"

"Because you should be with someone your own age. Someone who can give you children, a future. Not a divorced woman who's old enough to be your mother."

He sat up, looking offended. "First of all, you're not old enough to be my mother unless you had me at eighteen. Second, I don't want children. I want music. And I want you. Why is that so hard to believe?"

"Because it doesn't make sense."

"Love doesn't have to make sense."

"Love?"

He looked at me, vulnerable and brave. "Yes. Love. I'm in love with you, Sofia. I know it's fast, I know it's crazy, but I am. And if you don't feel the same way, that's okay. But don't push me away because you think I should want something different. I want you."

I started crying, and he pulled me into his arms.

"I love you too," I said. "I'm terrified, but I love you."

"Then let's be terrified together."

That was six months ago. We're still together, still neighbors, still making it work despite the age difference and the raised eyebrows and the occasional comment from people who think they know better. His family loves me. My friends are skeptical but supportive. And we're happy.

Is it forever? I don't know. Maybe he'll wake up one day and realize he wants someone younger. Maybe I'll get too old, too set in my ways. But right now, in this moment, we have each other. And that's enough.

Because sometimes the best things in life are the ones that don't make sense on paper. The ones that surprise you. The ones that remind you that it's never too late to start over, to take a risk, to let yourself feel alive.

And sometimes, the best love stories start with a noise complaint and a guitar playing through the wall at midnight.`
};
